import { useQuery } from '@tanstack/react-query';
import { gitClient } from '@terra/shared-rest-clients-git';
import { hasSourceCode } from '@terra/shared-utils';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';

type SourceCodeInfo = {
  isConnected: boolean;
  repositoryCount: number;
  platform: string | null;
};

export function useSourceCodeInfo(applicationId: string) {
  return useQuery({
    queryKey: ['source-code-info', applicationId],
    queryFn: async (): Promise<SourceCodeInfo> => {
      const authHeaders = await getAuthHeaders();
      const gitBaseOptions = {
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/git`,
        },
        extraHeaders: authHeaders,
      };

      const [gitIntegrationResult, reposResult] = await Promise.all([
        gitClient.getApplicationGitIntegrations({
          params: { applicationId },
          ...gitBaseOptions,
        }),
        gitClient.getApplicationRepositories({
          params: { applicationId },
          ...gitBaseOptions,
        }),
      ]);

      const gitIntegrations =
        gitIntegrationResult.status === 200
          ? (gitIntegrationResult.body.gitIntegrations ?? [])
          : [];

      const platform = gitIntegrations[0]?.platform ?? null;

      const repositories =
        reposResult.status === 200 ? reposResult.body.repositories || [] : [];

      const isConnected =
        hasSourceCode({ gitIntegrations }) || repositories.length > 0;

      return {
        isConnected,
        repositoryCount: repositories.length,
        platform,
      };
    },
    enabled: !!applicationId,
    staleTime: 60000,
  });
}
